<template>
  <div class="deriving-step">
    <!-- The birth. The chart is arithmetic and arrives on the first poll, so
         the wait is spent reading it rather than watching a log scroll. The
         pipeline's own lines come through as the note — static beats that are
         already true, interleaved with the parts still being written. -->
    <BirthReveal
      :chart="chart"
      :ready="status === 'completed'"
      :note="stepLog.length ? stepLog[stepLog.length - 1] : ''"
      :mural="mural"
    />

    <!-- The mural is the last screen. When the Mystic is ready it settles and
         the one action appears. The pipeline already claimed and filed the
         first drop, so the button only walks the seeker in. -->
    <Transition name="settle">
      <footer v-if="status === 'completed' && chart" class="birth-ready">
        <button class="publish" data-testid="become-begin" :disabled="publishing" @click="publish">
          <span class="publish-glyph" aria-hidden="true">✦</span>
          <span class="publish-label">{{ publishing ? 'A moment…' : 'Come, let\u2019s begin' }}</span>
          <span class="publish-arrow" aria-hidden="true">→</span>
        </button>
        <p v-if="publishError" class="publish-error">{{ publishError }}</p>
      </footer>
    </Transition>

    <div v-if="status === 'failed'" class="error-block">
      <p>The reading was interrupted. Please try again.</p>
      <button class="retry-btn" @click="$emit('failed')">← Try Again</button>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount } from 'vue'
import BirthReveal from '@/components/become-mystic/BirthReveal.vue'
import axios from 'axios'
import { static_store } from '@/store'
import { clientToday } from '@/utils/general-utils'

const props = defineProps({
  provisioningId: { type: Number, required: true },
})
const emit = defineEmits(['failed', 'published'])

const status = ref('processing')
const stepLog = ref(['Starting...'])
const chart = ref(null)
// Set once the pipeline has claimed: the button then has no request to make.
const claimed = ref(null)
// The mural: whatever the pipeline has made so far, refreshed every poll.
const mural = ref({})
let pollInterval = null
// A transient failure is worth retrying; an endless one is a dead screen. Two
// minutes at 2.5s, then give up and say so.
let misses = 0
const MAX_MISSES = 48

async function poll() {
  try {
    const token = localStorage.getItem('authToken')
    const resp = await axios.get(
      `${static_store.apiUrl}/reader/become-mystic/status/${props.provisioningId}/`,
      { headers: { Authorization: `Token ${token}` } }
    )
    const data = resp.data
    status.value = data.status

    // The chart arrives whole on the first poll — it is arithmetic, so it
    // exists the instant the fee is taken. The birth screen spends the
    // pipeline's seventy seconds reading it instead of watching a log.
    // The chart's geometry never changes, but what the Mystic SAYS about it
    // lands later (its own lines, then the bond) — so take the chart again
    // whenever those have arrived since the last poll.
    if (data.chart) {
      const c = chart.value
      const grew = !c
        || (data.chart.lines_spoken && !c.lines_spoken)
        || (data.chart.bond && !c.bond)
      if (grew) chart.value = data.chart
    }
    mural.value = {
      name: data.chosen_name || data.reader_name || '',
      introduction: data.introduction || '',
      portrait: data.portrait_url || '',
    }

    if (data.current_step && !stepLog.value.includes(data.current_step)) {
      stepLog.value.push(data.current_step)
    }
    if (data.claimed) {
      claimed.value = { subdomain: data.reader_subdomain, name: data.reader_name }
    }

    if (data.status === 'completed') {
      // Stay: the mural settles into its ready state here. There is no
      // second page to move to.
      clearInterval(pollInterval)
    } else if (data.status === 'failed') {
      clearInterval(pollInterval)
      emit('failed')
    }
  } catch (e) {
    const code = e && e.response && e.response.status
    if (code === 404 || code === 403) {
      // The run is gone, or is not this seeker's. Polling harder will not bring
      // it back: this is what left the panel spinning on "Starting…" forever
      // after a provisioning row was removed underneath it.
      clearInterval(pollInterval)
      emit('failed')
      return
    }
    // Anything else is worth retrying — but not indefinitely.
    misses += 1
    if (misses >= MAX_MISSES) {
      clearInterval(pollInterval)
      emit('failed')
    }
  }
}

// ── Ready: the one action ───────────────────────────────────────────────────
const publishing = ref(false)
const publishError = ref('')
async function publish() {
  if (publishing.value) return
  // The pipeline already claimed and filed the first drop: walk in now. The
  // request below is only the retry for a pipeline that could not claim.
  if (claimed.value) {
    emit('published', claimed.value)
    return
  }
  publishing.value = true
  publishError.value = ''
  try {
    const token = localStorage.getItem('authToken')
    const { data } = await axios.post(
      `${static_store.apiUrl}/reader/become-mystic/claim/${props.provisioningId}/`,
      { client_today: clientToday() },
      { withCredentials: true, headers: token ? { Authorization: `Token ${token}` } : {} })
    emit('published', { subdomain: data.reader_subdomain, name: data.reader_name })
  } catch (e) {
    publishError.value = e?.response?.data?.error
      || 'That did not complete. Nothing was lost — try again.'
  } finally {
    publishing.value = false
  }
}

onMounted(() => {
  pollInterval = setInterval(poll, 2500)
  poll()
})

onBeforeUnmount(() => {
  if (pollInterval) clearInterval(pollInterval)
})

defineExpose({ chart })
</script>

<style scoped>
.deriving-step { display: flex; flex-direction: column; align-items: center; padding: 8px 0 24px; gap: 12px; }

/* Ready: the one action under the mural. */
.birth-ready { width: 100%; max-width: 680px; display: grid; gap: 10px; padding: 0 20px; }
/* The Mystic's invitation: gold that breathes, a light that crosses it, and
   a step forward on hover. Reduced motion keeps the gold and drops the rest. */
.publish {
  position: relative; overflow: hidden; isolation: isolate;
  width: 100%; padding: 16px 22px; border-radius: 999px; cursor: pointer;
  display: inline-flex; align-items: center; justify-content: center; gap: 12px;
  font-family: 'Cinzel', serif; font-size: 16px; font-weight: 700; letter-spacing: 0.06em;
  color: #1a0c2e;
  background: linear-gradient(120deg, #f3d98a 0%, #d9b65a 45%, #f6e2a3 100%);
  border: 1px solid rgba(255, 233, 170, 0.8);
  box-shadow: 0 0 0 1px rgba(201, 169, 78, 0.25), 0 10px 30px rgba(201, 169, 78, 0.28), 0 0 42px rgba(201, 169, 78, 0.22);
  transition: transform 0.25s ease, box-shadow 0.25s ease;
  animation: publish-breathe 3.2s ease-in-out infinite;
}
.publish::after {
  content: ''; position: absolute; inset: -40% -60%; z-index: -1;
  background: linear-gradient(105deg, transparent 40%, rgba(255, 255, 255, 0.55) 50%, transparent 60%);
  transform: translateX(-70%);
  animation: publish-sweep 3.2s ease-in-out infinite;
}
.publish:hover:not(:disabled) {
  transform: translateY(-2px) scale(1.015);
  box-shadow: 0 0 0 1px rgba(255, 233, 170, 0.6), 0 14px 38px rgba(201, 169, 78, 0.4), 0 0 60px rgba(201, 169, 78, 0.35);
}
.publish:hover:not(:disabled) .publish-arrow { transform: translateX(5px); }
.publish:active:not(:disabled) { transform: translateY(0) scale(0.995); }
.publish:disabled { opacity: 0.6; cursor: not-allowed; animation: none; }
.publish:disabled::after { animation: none; }
.publish-glyph { font-size: 14px; color: rgba(26, 12, 46, 0.7); }
.publish-arrow { font-size: 18px; line-height: 1; transition: transform 0.25s ease; }
@keyframes publish-breathe {
  0%, 100% { box-shadow: 0 0 0 1px rgba(201, 169, 78, 0.25), 0 10px 30px rgba(201, 169, 78, 0.28), 0 0 42px rgba(201, 169, 78, 0.22); }
  50% { box-shadow: 0 0 0 1px rgba(255, 233, 170, 0.5), 0 12px 34px rgba(201, 169, 78, 0.38), 0 0 64px rgba(201, 169, 78, 0.34); }
}
@keyframes publish-sweep {
  0%, 35% { transform: translateX(-70%); }
  65%, 100% { transform: translateX(70%); }
}
@media (prefers-reduced-motion: reduce) {
  .publish, .publish::after { animation: none; }
  .publish::after { display: none; }
}
.publish-error { margin: 0; font-size: 13px; color: rgba(244, 67, 54, 0.95); }
.settle-enter-active { transition: opacity 0.9s ease, transform 0.9s ease; }
.settle-enter-from { opacity: 0; transform: translateY(10px); }
@media (prefers-reduced-motion: reduce) { .settle-enter-active { transition: none; } }
.error-block { text-align: center; }
.error-block p { color: rgba(255,150,150,0.8); font-size: 13px; margin-bottom: 12px; }
.retry-btn { background: none; border: 1px solid rgba(255,150,150,0.3); border-radius: 8px; color: rgba(255,150,150,0.8); padding: 8px 16px; cursor: pointer; font-family: inherit; font-size: 13px; }
</style>
